/**
 * Spec 0030 — read-only context beside the brand's guest-signup toggle. The URL
 * and label live on the *product*; this shows the effective source so nobody
 * toggles a link that can't appear. Shared by the Advanced form section and the
 * edit-in-preview slot popover.
 */
import { Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import type { ProductDto } from '@/api/types';
import { useCopy } from '@/hooks/useCopy';

export function GuestSignupHint({ product }: { product: ProductDto | null }) {
  const { t } = useCopy();
  const url = product?.guestSignupUrl;

  if (url) {
    const label =
      product?.guestSignupLabel || t('brands.guestSignup.defaultLabel', 'Continue as guest');
    return (
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          display: 'block',
          mt: 0.5,
        }}
      >
        {t('brands.guestSignup.effective', 'Links to {{url}} — shown as “{{label}}”.', {
          url,
          label,
        })}
      </Typography>
    );
  }

  return (
    <Typography
      variant="caption"
      sx={{
        color: 'text.secondary',
        display: 'block',
        mt: 0.5,
      }}
    >
      {product ? (
        <Link component={RouterLink} to={`/reference/products/${product.id}/edit`}>
          {t('brands.guestSignup.unset', 'Set the guest URL on the product')}
        </Link>
      ) : (
        t('brands.guestSignup.unset', 'Set the guest URL on the product')
      )}
    </Typography>
  );
}
